import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { colorServices } from "../../services/AllServices";
import * as Yup from "yup";

// Validation Schema
const colorSchema = Yup.object().shape({
  bg: Yup.string().required("Background color is required"),
  black: Yup.string().required("Black color is required"),
  white: Yup.string().required("White color is required"),
  p: Yup.string().required("Primary color is required"),
  blue: Yup.string().required("Blue color is required"),
  blueHover: Yup.string().required("Blue hover color is required"),
  accent: Yup.string().required("Accent color is required"),
  danger: Yup.string().required("Danger color is required"),
  border: Yup.string().required("Border color is required"),
});

const defaultColors = {
  bg: "#0F0D0A",
  black: "#1C1A12",
  white: "#FAF3DD",
  p: "#D6C7A1",
  blue: "#FFD700",
  blueHover: "#E6B800",
  accent: "#FFEB99",
  danger: "#FF5C5C",
  border: "#3A2F1B",
};

function AddEditColors() {
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ _id: "", createdAt: "", updatedAt: "" });
  const [data, setData] = useState(null);

  // Fetch color data
  const fetchColors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await colorServices.get();
      const resData = response?.data || {};
      // Save meta fields separately
      setMeta({
        _id: resData?._id || "",
        createdAt: resData?.createdAt || "",
        updatedAt: resData?.updatedAt || "",
      });
      // Remove meta fields from color values
      const { _id, createdAt, updatedAt, __v, ...colors } = resData;
      setData(colors);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchColors();
  }, [fetchColors]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: colorSchema,
    initialValues: {
      ...defaultColors,
      ...data,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await colorServices.update(values);
        toast.success(response?.data?.message, { toastId: "success" });
        fetchColors();
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit, setValues } = formik;

  // Reset to default colors
  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset to default colors?")) {
      setValues(defaultColors);
      try {
        const response = await colorServices.update(defaultColors);
        toast.success(response?.data?.message, { toastId: "success" });
        fetchColors();
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  return (
    <Box>
      <Helmet>
        <title>Update Theme Colors</title>
      </Helmet>
      <CardLayout>
        <CardHeader title="Update Theme Colors" />
        <Divider />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {Object.keys(values).map((key) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Typography variant="subtitle2" gutterBottom>
                      {key}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {/* Color Picker */}
                      <input
                        type="color"
                        name={key}
                        value={values[key]}
                        onChange={handleChange}
                        style={{
                          width: 50,
                          height: 50,
                          border: "none",
                          cursor: "pointer",
                          borderRadius: "8px",
                          padding: 0,
                          background: "none",
                        }}
                      />
                      {/* Hex Input */}
                      <TextField
                        fullWidth
                        name={key}
                        value={values[key]}
                        onChange={handleChange}
                        error={Boolean(touched[key] && errors[key])}
                        helperText={touched[key] && errors[key]}
                        placeholder="#FFFFFF"
                        inputProps={{ maxLength: 7 }}
                      />
                    </Stack>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      Save Colors
                    </LoadingButton>
                    <LoadingButton
                      fullWidth
                      size="large"
                      variant="outlined"
                      color="error"
                      onClick={handleReset}
                      disabled={loading}
                    >
                      Reset to Default
                    </LoadingButton>
                  </Stack>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditColors;
